/**
 * MethodologyDialog.tsx
 * Phase 5, Item 6 — Dialog for editing a single TestCatalogItem's methodology block.
 *
 * Opened by the "Methodology" button that appears per row in TestCatalogPage.
 *
 * Behavior:
 *   - Opens and fetches the current active block (if any) via GET /:key/methodology.
 *   - Admin can edit Heading and Body fields.
 *   - "Save" calls POST /:key/methodology (upsert — backend handles version bump).
 *   - "Delete" calls DELETE /:key/methodology (soft-delete).
 *   - Shows "Version N — Last updated by X on Y" when a block exists.
 *
 * All API calls go through testCatalogService (apiClient-backed).
 * Colors use theme tokens only — no hex literals.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  CircularProgress,
  Alert,
  IconButton,
  Chip,
} from '@mui/material';
import { Close, Delete, Save } from '@mui/icons-material';
import type { ProposalMethodologyBlock } from '../../../types/schedule';
import {
  getMethodologyBlock,
  upsertMethodologyBlock,
  deleteMethodologyBlock,
} from '../../../services/testCatalogService';

interface Props {
  open: boolean;
  onClose: () => void;
  testCatalogItemKey: string;
  testName: string;
}

function formatDateTime(iso: string): string {
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: 'numeric', month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

export const MethodologyDialog: React.FC<Props> = ({
  open,
  onClose,
  testCatalogItemKey,
  testName,
}) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [block, setBlock] = useState<ProposalMethodologyBlock | null>(null);

  // Editable draft fields
  const [heading, setHeading] = useState('');
  const [body, setBody] = useState('');

  const fetchBlock = useCallback(async () => {
    setLoading(true);
    setErrorMsg(null);
    try {
      const data = await getMethodologyBlock(testCatalogItemKey);
      setBlock(data);
      setHeading(data?.Heading ?? '');
      setBody(data?.Body ?? '');
    } catch {
      setErrorMsg('Failed to load methodology block.');
    } finally {
      setLoading(false);
    }
  }, [testCatalogItemKey]);

  useEffect(() => {
    if (open) {
      fetchBlock();
    } else {
      // Reset state when dialog closes
      setBlock(null);
      setHeading('');
      setBody('');
      setErrorMsg(null);
    }
  }, [open, fetchBlock]);

  const handleSave = async () => {
    setSaving(true);
    setErrorMsg(null);
    try {
      const saved = await upsertMethodologyBlock(testCatalogItemKey, {
        Heading: heading.trim() || null,
        Body: body.trim() || null,
      });
      setBlock(saved);
    } catch (err) {
      const msg =
        (err as { response?: { data?: { error?: string } } }).response?.data?.error ??
        (err instanceof Error ? err.message : 'Failed to save methodology block.');
      setErrorMsg(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    setErrorMsg(null);
    try {
      await deleteMethodologyBlock(testCatalogItemKey);
      setBlock(null);
      setHeading('');
      setBody('');
    } catch (err) {
      const msg =
        (err as { response?: { data?: { error?: string } } }).response?.data?.error ??
        (err instanceof Error ? err.message : 'Failed to delete methodology block.');
      setErrorMsg(msg);
    } finally {
      setDeleting(false);
    }
  };

  const isDirty =
    heading !== (block?.Heading ?? '') ||
    body !== (block?.Body ?? '');

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          pb: 1,
        }}
      >
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600, lineHeight: 1.3 }}>
            Methodology Block
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
            {testName}
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small" sx={{ mt: -0.5 }}>
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {errorMsg && (
          <Alert
            severity="error"
            sx={{ mb: 2 }}
            onClose={() => setErrorMsg(null)}
          >
            {errorMsg}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={32} />
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Version + audit info */}
            {block && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Chip
                  label={`Version ${block.Version}`}
                  size="small"
                  variant="outlined"
                  color="primary"
                />
                <Typography variant="caption" color="text.secondary">
                  Last updated by{' '}
                  <strong>{block.sysLastModifiedUser}</strong>{' '}
                  on {formatDateTime(block.sysLastModifiedDate)}
                </Typography>
              </Box>
            )}

            {!block && !loading && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ fontStyle: 'italic' }}
              >
                No methodology block exists for this test yet. Fill in the fields below and click Save to create one.
              </Typography>
            )}

            <TextField
              label="Heading"
              value={heading}
              onChange={(e) => setHeading(e.target.value)}
              fullWidth
              size="small"
              inputProps={{ maxLength: 250 }}
              helperText="Short section title (e.g. 'Direct Shear Testing')"
            />

            <TextField
              label="Body"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              fullWidth
              multiline
              minRows={8}
              maxRows={20}
              helperText="Methodology paragraph that will be included in the generated proposal."
            />
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ justifyContent: 'space-between', px: 3, py: 1.5 }}>
        {/* Left side: Delete (only if a block exists) */}
        <Box>
          {block && (
            <Button
              color="error"
              variant="outlined"
              startIcon={<Delete />}
              onClick={handleDelete}
              disabled={deleting || saving || loading}
            >
              {deleting ? 'Deleting…' : 'Delete Block'}
            </Button>
          )}
        </Box>

        {/* Right side: Cancel / Save */}
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button onClick={onClose} variant="outlined" disabled={saving || deleting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            startIcon={<Save />}
            onClick={handleSave}
            disabled={saving || deleting || loading || !isDirty}
          >
            {saving ? 'Saving…' : block ? 'Update' : 'Save'}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};
